import { AppError } from './AppError'

export type ResponseJsonType = {
  data: object | object[] | null
}

export type FetchResponse<
  ResponseJson extends ResponseJsonType
> = ResponseJson & {
  status: number
  error?: AppError | null
}

/**
 * Make the request, failing with a connection error when it never
 * reaches the server.
 *
 * `fetch` rejects only when the request could not complete — a dropped
 * connection, a DNS failure, a blocked request. An HTTP error status
 * resolves normally and is handled by the caller. That makes the
 * rejection itself enough to classify the failure, without reading its
 * browser-specific message.
 */
async function fetchOrConnectionError(
  url: string,
  init: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (err) {
    throw AppError.asConnectionError(err)
  }
}

/**
 * Turn a response into the envelope callers expect.
 *
 * Reads the body as text first: an error response may not be JSON at
 * all — a proxy's HTML 502 page is the common one — and the real status
 * is more use to the caller than an opaque parse failure.
 */
async function readJsonResponse<
  ResponseJson extends ResponseJsonType
>(
  res: Response,
  method: string,
  endpoint: string
): Promise<FetchResponse<ResponseJson>> {
  const rawBody = await res.text()
  let parsed: unknown = null
  if (rawBody.length > 0) {
    try {
      parsed = JSON.parse(rawBody)
    } catch {
      // Leave parsed as null and let the status speak.
    }
  }
  if (res.ok) {
    if (parsed === null) {
      throw new Error(
        `Expected JSON response from ${method.toUpperCase()} ${endpoint} but got ${res.status} ${res.statusText} with ${rawBody.length === 0 ? 'empty body' : 'non-JSON body'}`
      )
    }
    return { status: res.status, ...(parsed as ResponseJson) }
  }
  return {
    status: res.status,
    data: null,
    error: AppError.fromResponse(
      res,
      parsed ?? (rawBody.length > 0 ? rawBody : null)
    ),
  } as FetchResponse<ResponseJson>
}

/**
 * The envelope for a request that never produced a response — including
 * the connection errors raised by `fetchOrConnectionError`.
 */
function toErrorResponse<
  ResponseJson extends ResponseJsonType
>(err: unknown): FetchResponse<ResponseJson> {
  const error = AppError.fromError(err)
  return {
    status: error.status,
    data: null,
    error,
  } as FetchResponse<ResponseJson>
}

export interface FetchWithAuthOptions<Body = undefined> {
  baseUrl: string
  accessToken: string | null
  method: 'get' | 'post' | 'put' | 'delete'
  endpoint: string
  searchParams?: URLSearchParams
  body?: Body
  signal?: AbortSignal
}

export async function fetchWithAuth<
  ResponseJson extends ResponseJsonType,
  Body = undefined
>({
  accessToken,
  baseUrl,
  endpoint,
  method,
  searchParams,
  body,
  signal,
}: FetchWithAuthOptions<Body>): Promise<
  FetchResponse<ResponseJson>
> {
  let url = `${baseUrl}${endpoint}`
  if (searchParams?.size) {
    url += `?${searchParams}`
  }
  try {
    const res = await fetchOrConnectionError(url, {
      method: method.toUpperCase(),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
      },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
      signal,
    })
    return await readJsonResponse<ResponseJson>(
      res,
      method,
      endpoint
    )
  } catch (err) {
    return toErrorResponse<ResponseJson>(err)
  }
}

export interface FetchJsonOptions<Body = undefined> {
  method: 'get' | 'post'
  baseUrl?: string
  endpoint: string
  query?: URLSearchParams
  body?: Body
  signal?: AbortSignal
}

export async function fetchJson<
  ResponseJson extends ResponseJsonType,
  Body = undefined
>({
  baseUrl,
  endpoint,
  query,
  method,
  body,
  signal,
}: FetchJsonOptions<Body>): Promise<
  FetchResponse<ResponseJson>
> {
  try {
    const res = await fetchOrConnectionError(
      `${baseUrl}${endpoint}${
        query?.size ? `?${query.toString()}` : ''
      }`,
      {
        method: method.toUpperCase(),
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: body ? JSON.stringify(body) : undefined,
        signal,
      }
    )
    return await readJsonResponse<ResponseJson>(
      res,
      method,
      endpoint
    )
  } catch (err) {
    return toErrorResponse<ResponseJson>(err)
  }
}
